import {
  onlineManager,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";

// Hard-coded so the guard depends only on onlineManager + the notification
// context (both safe in any render context). This blocks a mutation BEFORE its
// optimistic onMutate runs, so local state never flickers offline. The
// window.fetch choke point (offlineWriteGuard.ts) is the backstop that refuses
// any write that still reaches the network.
const OFFLINE_MESSAGE = "You need to be online to use this.";

/** Expected rejection when mutateAsync is blocked offline (not a bug). */
export class OfflineMutationBlockedError extends Error {
  constructor() {
    super("offline");
    this.name = "OfflineMutationBlockedError";
  }
}

export function isOfflineMutationBlockedError(error: unknown): boolean {
  return error instanceof OfflineMutationBlockedError;
}

/**
 * Drop-in replacement for useMutation that BLOCKS the mutation while the device
 * is offline (and notifies the user) instead of pausing-and-queuing it.
 *
 * Use this for every data-changing mutation, including animal moves. The
 * persisted query cache is read-only offline; writes must wait for connectivity.
 */
export function useGuardedMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData, TError, TVariables, TContext> {
  const mutation = useMutation(options);
  const { notify } = useNotification();

  const mutate = ((variables, mutateOptions) => {
    if (!onlineManager.isOnline()) {
      notify(OFFLINE_MESSAGE, "error");
      return;
    }
    return mutation.mutate(variables, mutateOptions);
  }) as typeof mutation.mutate;

  const mutateAsync = ((variables, mutateOptions) => {
    if (!onlineManager.isOnline()) {
      notify(OFFLINE_MESSAGE, "error");
      return Promise.reject(new OfflineMutationBlockedError());
    }
    return mutation.mutateAsync(variables, mutateOptions);
  }) as typeof mutation.mutateAsync;

  return { ...mutation, mutate, mutateAsync };
}
